import React from "react";
import { citationLabel, citationStyle, confidenceClass, confidenceLabel, sourceCategory } from "../lib/citations";

// Evidence-first labels, not outcome predictions — "Strong case" reads as a forecast of
// whether an appeal will win; "Well-evidenced" says only what is actually true, that this
// ground is backed by cited estate-rule text and/or matching precedent on file. Rule 2
// ("never predict an outcome"); see planning/FINDING-appeal-scoring-rule2-2026-08-24.md.
const VERDICT_STYLES = {
    strong: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Well-evidenced" },
    moderate: { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A", label: "Partly evidenced" },
    weak: { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA", label: "Little evidence found" },
    insufficient: { bg: "#F1F5F9", text: "#475569", border: "#CBD5E1", label: "No evidence found" },
};

const STRENGTH_STYLES = {
    high: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Directly cited" },
    medium: { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", label: "Partially cited" },
    low: { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A", label: "Weakly cited" },
};

const PRECEDENT_STYLES = {
    successful: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Past success" },
    failed: { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA", label: "Past failure" },
    none: { bg: "#F8FAFC", text: "#475569", border: "#E2E8F0", label: "No precedent" },
};

const CHECK_STYLES = {
    complete: { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0", label: "Complete" },
    recommended: { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A", label: "Recommended" },
    missing: { bg: "#FEF2F2", text: "#B91C1C", border: "#FECACA", label: "Missing" },
};

function Pill({ style, dataTestid }) {
    return (
        <span
            data-testid={dataTestid}
            className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded"
            style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
        >
            {style.label}
        </span>
    );
}

export default function AppealAnalysisCard({ analysis, selectedIds, onToggleGround, onContinue, onBack, onChecklistAction, submitting }) {
    if (!analysis) return null;
    const v = VERDICT_STYLES[analysis.verdict] || VERDICT_STYLES.insufficient;
    const score = analysis.overall_score ?? 0;

    return (
        <section className="space-y-6" data-testid="appeal-analysis-card">
            <div className="border border-slate-200 bg-white rounded-lg p-5">
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-500">Evidence review</div>
                        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">How your case matches the record</h2>
                        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                            Estate rules, past successful appeals and past failed appeals from the knowledge base were used as the yardstick.
                            Tick the grounds you want included in your letter, then continue.
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-xs uppercase tracking-widest text-slate-500">Evidence match</div>
                        <div className="font-heading text-3xl font-bold text-slate-900 leading-none" data-testid="analysis-score">{score}<span className="text-base text-slate-500">/100</span></div>
                        <div className="mt-2"><Pill style={v} dataTestid="analysis-verdict" /></div>
                    </div>
                </div>

                {/* Evidence-match bar — how much of the estate's own record supports the grounds below, not a forecast */}
                <div className="mt-4 h-2 w-full bg-slate-100 rounded-full overflow-hidden" aria-label="Evidence match">
                    <div
                        data-testid="analysis-score-bar"
                        className="h-full"
                        style={{
                            width: `${Math.max(2, Math.min(100, score))}%`,
                            backgroundColor: v.text,
                            transition: "width 240ms ease-out",
                        }}
                    />
                </div>

                {analysis.analysis_disclaimer && (
                    <p className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-3" data-testid="analysis-disclaimer">
                        {analysis.analysis_disclaimer}
                    </p>
                )}
            </div>

            {analysis.evidence_checklist?.length > 0 && (
                <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="evidence-checklist">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Evidence checklist</div>
                    <div className="mt-3 space-y-2">
                        {analysis.evidence_checklist.map((item) => {
                            const style = CHECK_STYLES[item.status] || CHECK_STYLES.recommended;
                            return (
                                <div key={item.id} className="border border-slate-200 rounded-md p-3" data-testid={`evidence-check-${item.id}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="font-heading text-sm font-semibold text-slate-900">{item.label}</div>
                                        <Pill style={style} dataTestid={`evidence-check-status-${item.id}`} />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">{item.reason}</p>
                                    {item.status !== "complete" && (
                                        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                                            <div className="text-xs text-slate-700 font-medium">Next: {item.action}</div>
                                            {onChecklistAction && (
                                                <button
                                                    type="button"
                                                    onClick={() => onChecklistAction(item)}
                                                    className="inline-flex items-center px-2.5 py-1.5 rounded border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
                                                    data-testid={`evidence-check-action-${item.id}`}
                                                >
                                                    Do this
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="space-y-3" data-testid="grounds-list">
                <div className="text-xs uppercase tracking-widest text-slate-500">Possible grounds to argue</div>
                {analysis.grounds?.length === 0 && (
                    <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-4" data-testid="grounds-empty">
                        No specific grounds identified. Consider gathering more evidence (permits, deeds, photos) and re-running the analysis.
                    </div>
                )}
                {analysis.grounds?.map((g) => {
                    const checked = selectedIds.includes(g.id);
                    const strength = STRENGTH_STYLES[g.strength] || STRENGTH_STYLES.medium;
                    const precedent = PRECEDENT_STYLES[g.precedent] || PRECEDENT_STYLES.none;
                    return (
                        <label
                            key={g.id}
                            data-testid={`ground-row-${g.id}`}
                            className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors duration-150 ${
                                checked ? "border-slate-700 bg-slate-50" : "border-slate-200 bg-white hover:bg-slate-50"
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => onToggleGround?.(g.id)}
                                className="mt-1 h-4 w-4 accent-slate-700"
                                data-testid={`ground-checkbox-${g.id}`}
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-mono text-slate-500">{g.id}</span>
                                    <h3 className="font-heading text-sm font-semibold text-slate-900" data-testid={`ground-title-${g.id}`}>{g.title}</h3>
                                    <Pill style={strength} dataTestid={`ground-strength-${g.id}`} />
                                    <Pill style={precedent} dataTestid={`ground-precedent-${g.id}`} />
                                </div>
                                <p className="text-sm text-slate-700 mt-1">{g.description}</p>
                                {g.rationale && (
                                    <p className="text-xs text-slate-500 mt-1 italic">Why this applies: {g.rationale}</p>
                                )}
                                {g.rule_citations?.length > 0 && (
                                    <ul className="mt-2 space-y-1">
                                        {g.rule_citations.map((c, i) => {
                                            const style = citationStyle(c);
                                            return (
                                                <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                                                    <span
                                                        className="inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap"
                                                        style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
                                                    >
                                                        {citationLabel(c)}
                                                    </span>
                                                    <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap ${confidenceClass(c)}`}>
                                                        {confidenceLabel(c)}
                                                    </span>
                                                    <span>
                                                        {c.document_title && <strong>{c.document_title}: </strong>}
                                                        <span className="text-slate-500">{sourceCategory(c)}. </span>
                                                        {c.snippet}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </label>
                    );
                })}
            </div>

            {(analysis.recommendations?.length > 0 || analysis.risks?.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {analysis.recommendations?.length > 0 && (
                        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4" data-testid="analysis-recommendations">
                            <div className="text-xs uppercase tracking-widest text-emerald-700">Recommendations</div>
                            <ul className="mt-2 space-y-1 text-sm text-emerald-900 list-disc pl-5">
                                {analysis.recommendations.map((r, i) => <li key={i} data-testid={`recommendation-${i}`}>{r}</li>)}
                            </ul>
                        </div>
                    )}
                    {analysis.risks?.length > 0 && (
                        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4" data-testid="analysis-risks">
                            <div className="text-xs uppercase tracking-widest text-amber-700">Risks</div>
                            <ul className="mt-2 space-y-1 text-sm text-amber-900 list-disc pl-5">
                                {analysis.risks.map((r, i) => <li key={i} data-testid={`risk-${i}`}>{r}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-slate-700 underline"
                    data-testid="analysis-back-button"
                >
                    ← Back to context
                </button>
                <button
                    type="button"
                    onClick={onContinue}
                    disabled={submitting || selectedIds.length === 0}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
                    data-testid="generate-letter-button"
                >
                    {submitting ? "Drafting letter…" : `Generate letter with ${selectedIds.length} ground${selectedIds.length === 1 ? "" : "s"}`}
                </button>
            </div>
        </section>
    );
}
